import AddIcon from '@mui/icons-material/Add'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import ContentPasteIcon from '@mui/icons-material/ContentPaste'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  ListItemButton,
  ListItemText,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { type SxProps, type Theme } from '@mui/material/styles'
import { TableVirtuoso } from 'react-virtuoso'

import BrandState from '../../components/BrandState'
import ResizeHandle from '../../components/ResizeHandle'
import {
  ColumnFilterIcon,
  ColumnFilterPopover,
  DataframeBody,
  FilterBar,
  FilterButton,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  filterBarBtnSx,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
  useRowSelection,
  type ColumnFilterAnchorPosition,
  type ColumnFilterKind,
} from '../../components/dataList'
import { useColumnWidths } from '../../hooks/useColumnWidths'
import { HIDE_ON_MOBILE_INLINE, HIDE_ON_TABLET_INLINE, TABLE_PAGINATION_COMPACT } from '../../theme/responsive'
import { usePagePermission } from '../../hooks/usePagePermission'
import { useSnackbar } from '../../hooks/useSnackbar'
import {
  FUTURES_ALL_ROWS_PAGE_SIZE,
  useBulkCreateFuturesSpreadsMutation,
  useDeleteFuturesSpreadMutation,
  useGetFuturesOrderFlagsQuery,
  useGetFuturesOrderPartnersQuery,
  useGetFuturesOrderTradeTypesQuery,
  useGetFuturesSpreadsFilterValuesQuery,
  useGetFuturesSpreadsQuery,
  useGetSpreadSymbolsQuery,
} from '../../store/api/futuresControlApi'
import { futuresApiErrorMessage } from './futuresApiErrorMessage'
import { FuturesColumnsMenu } from './FuturesColumnsMenu'
import SpreadsFormDialog, { type SpreadFormMode, type SpreadFormRow } from './SpreadsFormDialog'
import { useFuturesColumns } from './useFuturesColumns'
import {
  futuresFormDialogActionBtnSx,
  futuresFormDialogActionsBoxSx,
  futuresFormDialogTitleSx,
  futuresFormDialogTitleTextSx,
  futuresSaleContractDialogContentSx,
  futuresSaleContractDialogPaperProps,
  futuresSaleContractDialogTitleSx,
} from './futuresAccessUi'

const actionToolbarBtnSx: SxProps<Theme> = {
  minWidth: 0,
  px: 0.75,
  py: 0.2,
  minHeight: 26,
  fontSize: '0.72rem',
  lineHeight: 1.1,
  textTransform: 'none',
}

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const formatAccessDate = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  const year = `${d.getFullYear()}`
  return `${day}-${month}-${year}`
}

const formatAccessDateTime = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  const year = `${d.getFullYear()}`
  const hh = `${d.getHours()}`.padStart(2, '0')
  const mm = `${d.getMinutes()}`.padStart(2, '0')
  return `${day}-${month}-${year} ${hh}:${mm}`
}

const formatAccessNumber = (value: number): string => {
  const abs = Math.abs(value)
  const formatted = Number.isInteger(value)
    ? abs.toLocaleString('en-US')
    : abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return value < 0 ? `(${formatted})` : formatted
}

const toIsoDateOnly = (value: unknown): string | null => {
  if (!value) return null
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return null
  const yyyy = d.getFullYear()
  const mm = `${d.getMonth() + 1}`.padStart(2, '0')
  const dd = `${d.getDate()}`.padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

const toggleInSet = (set: Set<string>, value: string): Set<string> => {
  const next = new Set(set)
  if (next.has(value)) next.delete(value)
  else next.add(value)
  return next
}

type ColumnKind = 'text' | 'date' | 'datetime' | 'number'
type ColumnDef = {
  key: string
  label: string
  kind?: ColumnKind
  align?: 'right'
  filter?: ColumnFilterKind
}

/**
 * Thứ tự cột: Order Number → Date → ... → To Month 2 (giữa leg2) → ... → Updated.
 * Source: `public_mv_orders_spreads` (query_FuturesQrySpreads).
 */
/** Cột freeze khi scroll ngang — chỉ Order No. */
const FROZEN_KEYS: ReadonlySet<string> = new Set(['orderNumber'])
const FROZEN_FALLBACK_WIDTH: Record<string, number> = {
  orderNumber: 130,
}

const COLUMNS: ColumnDef[] = [
  { key: 'orderNumber', label: 'Order No' },
  { key: 'date', label: 'Date', kind: 'date' },
  { key: 'buyOrSell', label: 'B/S', filter: 'checkboxes' },
  { key: 'symbol', label: 'Symbol', filter: 'checkboxes' },
  { key: 'filledPrice', label: 'Filled Price', kind: 'number', align: 'right' },
  { key: 'typeOfTrade', label: 'Type of Trade', filter: 'checkboxes' },
  { key: 'lot', label: 'Lot', kind: 'number', align: 'right' },
  { key: 'partner', label: 'Partner', filter: 'checkboxes' },
  { key: 'rollStep', label: 'Roll Step', kind: 'number', align: 'right' },
  { key: 'orderNo1', label: 'Order No 1' },
  { key: 'date1', label: 'Date 1', kind: 'date' },
  { key: 'buyOrSell1', label: 'B/S 1' },
  { key: 'fromMonth1', label: 'From Month 1' },
  { key: 'fromPrice1', label: 'From Price 1', kind: 'number', align: 'right' },
  { key: 'type1', label: 'Type 1' },
  { key: 'lot1', label: 'Lot 1', kind: 'number', align: 'right' },
  { key: 'partner1', label: 'Partner 1' },
  { key: 'orderNo2', label: 'Order No 2' },
  { key: 'date2', label: 'Date 2', kind: 'date' },
  { key: 'buyOrSell2', label: 'B/S 2' },
  { key: 'toMonth2', label: 'To Month 2' },
  { key: 'toPrice2', label: 'To Price 2', kind: 'number', align: 'right' },
  { key: 'type2', label: 'Type 2' },
  { key: 'lot2', label: 'Lot 2', kind: 'number', align: 'right' },
  { key: 'partner2', label: 'Partner 2' },
  { key: 'dateUpdated', label: 'Updated Date', kind: 'datetime' },
  { key: 'personUpdated', label: 'Updated Person' },
]

const NON_SUM = new Set<string>([
  'filledPrice',
  'fromPrice1',
  'toPrice2',
])

const STORAGE_COLS_SPREADS = 'futures.spreads-orders'

const BUY_SELL_OPTIONS = ['BUY', 'SELL'] as const

/** Mass-upload row mirrors Access form `frmFuturesQrySpreadsOrderAddMany` columns. */
type MassUploadRow = {
  date: string
  buyOrSell: string
  symbol: string
  rollStep: string
  filledPrice: string
  typeOfTrade: string
  flag: string
  lot: string
  partner: string
  fromPrice: string
}

const todayIso = () => new Date().toISOString().slice(0, 10)

const emptyMassRow = (): MassUploadRow => ({
  date: todayIso(),
  buyOrSell: '',
  symbol: '',
  rollStep: '',
  filledPrice: '',
  typeOfTrade: '',
  flag: '',
  lot: '',
  partner: '',
  fromPrice: '',
})

const MASS_FIELDS: (keyof MassUploadRow)[] = [
  'date',
  'buyOrSell',
  'symbol',
  'rollStep',
  'filledPrice',
  'typeOfTrade',
  'flag',
  'lot',
  'partner',
  'fromPrice',
]

// Human-readable column titles (MASS_FIELDS order). Emitted as the first row when
// copying so Excel gets headers, and detected + skipped on paste so a block copied
// with headers round-trips cleanly.
const MASS_HEADERS: Record<keyof MassUploadRow, string> = {
  date: 'Date',
  buyOrSell: 'Buy/Sell',
  symbol: 'Symbol',
  rollStep: 'Roll Step',
  filledPrice: 'Filled Price',
  typeOfTrade: 'Type of Trade',
  flag: 'Flag',
  lot: 'Lot',
  partner: 'Partner',
  fromPrice: 'From Price',
}

// A pasted row is a header (not data) when its leading cells match our column
// titles (case-insensitive). Require ≥2 matches so a genuine data row whose first
// cell happens to read "Date" isn't dropped.
function isMassHeaderRow(cells: string[]): boolean {
  const expected = MASS_FIELDS.map((f) => MASS_HEADERS[f].toLowerCase())
  let matches = 0
  cells.forEach((c, i) => {
    if (i < expected.length && c.trim().toLowerCase() === expected[i]) matches += 1
  })
  return matches >= 2
}

function buildIsoDate(year: number, month: number, day: number): string | null {
  if (month < 1 || month > 12) return null
  if (day < 1 || day > 31) return null
  if (year < 1900 || year > 2999) return null
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

function parsePastedDate(raw: string): string {
  const s = raw.trim()
  if (!s) return ''
  const iso = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/)
  if (iso) {
    const year = parseInt(iso[1], 10)
    const a = parseInt(iso[2], 10)
    const b = parseInt(iso[3], 10)
    const direct = buildIsoDate(year, a, b)
    if (direct) return direct
    const swapped = buildIsoDate(year, b, a)
    if (swapped) return swapped
    return ''
  }
  const sep = s.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})$/)
  if (sep) {
    const p1 = parseInt(sep[1], 10)
    const p2 = parseInt(sep[2], 10)
    let yearStr = sep[3]
    if (yearStr.length === 2) yearStr = `20${yearStr}`
    const year = parseInt(yearStr, 10)
    let day: number, month: number
    if (p1 > 12 && p2 <= 12) { day = p1; month = p2 }
    else if (p2 > 12 && p1 <= 12) { day = p2; month = p1 }
    else { day = p1; month = p2 }
    const result = buildIsoDate(year, month, day)
    if (result) return result
    return ''
  }
  const t = new Date(s)
  if (!Number.isNaN(t.getTime())) {
    const result = buildIsoDate(t.getFullYear(), t.getMonth() + 1, t.getDate())
    if (result) return result
  }
  return ''
}

function safeDateValue(v: string): string {
  return /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/.test(v) ? v : ''
}

// Returns canonical "BUY"/"SELL" if recognised, "" if blank, or null if the
// value cannot map to one of the two options.
function normalizeBuySell(raw: string): string | null {
  const s = raw.trim().toUpperCase()
  if (s === '') return ''
  if (s === 'B' || s === 'BUY') return 'BUY'
  if (s === 'S' || s === 'SELL') return 'SELL'
  return null
}

// `decimal` allows a single dot; otherwise integer only. Returns "" for blank,
// or null when the value contains anything other than digits/sign/decimal.
function coerceNumericStrict(raw: string, decimal: boolean): string | null {
  const s = raw.replace(/[,\s_]/g, '').trim()
  if (s === '') return ''
  if (decimal) {
    return /^-?\d+(?:\.\d+)?$/.test(s) ? s : null
  }
  return /^-?\d+$/.test(s) ? s : null
}

// Field-level coercion used by paste. Returns the normalised value, "" for
// blank, or null when the cell content should be rejected.
function coerceMassField(field: keyof MassUploadRow, raw: string): string | null {
  const v = raw.replace(/\r$/, '')
  if (field === 'date') return parsePastedDate(v)
  if (field === 'buyOrSell') return normalizeBuySell(v)
  if (field === 'rollStep') return coerceNumericStrict(v, /*decimal*/ false)
  if (field === 'filledPrice' || field === 'lot' || field === 'fromPrice') {
    return coerceNumericStrict(v, /*decimal*/ true)
  }
  // For text/combobox fields keep the raw string; option-matching happens
  // inside the component where the lookup options are available.
  return v.trim()
}

function parseTsv(text: string): string[][] {
  const trimmed = text.replace(/\r/g, '').replace(/\n$/, '')
  if (!trimmed) return []
  return trimmed.split('\n').map((line) => line.split('\t'))
}

function massNum(v: string): number | undefined {
  if (v.trim() === '') return undefined
  const n = Number(v)
  return Number.isFinite(n) ? n : undefined
}

function toMassPayload(row: MassUploadRow) {
  return {
    date: row.date || undefined,
    buyOrSell: row.buyOrSell || undefined,
    symbol: row.symbol || undefined,
    rollStep: massNum(row.rollStep),
    filledPrice: massNum(row.filledPrice),
    typeOfTrade: row.typeOfTrade || undefined,
    flag: row.flag || undefined,
    lot: massNum(row.lot),
    partner: row.partner || undefined,
    fromPrice: massNum(row.fromPrice),
  }
}

type MassLookupOptions = {
  symbol: string[]
  typeOfTrade: string[]
  partner: string[]
  flag: string[]
}

// Per-row cross-check verdict — drives the ✓/✗ status column, the red-cell
// highlighting, the error summary panel, and the enabled state of Upload. This is
// the SAME rule set the Upload-time loop (`runMassUpload`) enforces, factored into
// a pure function so what enables Upload == what inserts.
type MassVerdict = { ok: boolean; badFields: Set<keyof MassUploadRow>; messages: string[] }

function validateSpreadRow(row: MassUploadRow, lookups: MassLookupOptions): MassVerdict {
  const badFields = new Set<keyof MassUploadRow>()
  const messages: string[] = []
  const fail = (field: keyof MassUploadRow, msg: string) => {
    badFields.add(field)
    messages.push(msg)
  }
  const inList = (value: string, list: string[]): boolean =>
    list.some((opt) => opt.toLowerCase() === value.trim().toLowerCase())

  if (!row.date.trim()) fail('date', 'Missing Date')
  else if (!/^\d{4}-\d{2}-\d{2}$/.test(row.date.trim())) fail('date', 'Invalid Date')

  const bs = row.buyOrSell.trim().toUpperCase()
  if (bs !== 'BUY' && bs !== 'SELL') fail('buyOrSell', 'Buy/Sell must be BUY or SELL')

  if (!row.symbol.trim()) fail('symbol', 'Missing Symbol')
  else if (row.symbol.trim().length < 6) fail('symbol', 'Symbol must be ≥ 6 characters')
  else if (lookups.symbol.length && !inList(row.symbol, lookups.symbol)) fail('symbol', 'Symbol not in list')

  const rollStep = massNum(row.rollStep)
  if (rollStep == null || rollStep <= 0) fail('rollStep', 'Roll Step must be > 0')

  const filledPrice = massNum(row.filledPrice)
  if (filledPrice == null || filledPrice <= 0) fail('filledPrice', 'Filled Price must be > 0')

  if (!row.typeOfTrade.trim()) fail('typeOfTrade', 'Missing Type of Trade')
  else if (lookups.typeOfTrade.length && !inList(row.typeOfTrade, lookups.typeOfTrade))
    fail('typeOfTrade', 'Type of Trade not in list')

  if (!row.flag.trim()) fail('flag', 'Missing Flag')
  else if (lookups.flag.length && !inList(row.flag, lookups.flag)) fail('flag', 'Flag not in list')

  const lot = massNum(row.lot)
  if (lot == null || lot <= 0) fail('lot', 'Lot must be > 0')

  if (!row.partner.trim()) fail('partner', 'Missing Partner')
  else if (lookups.partner.length && !inList(row.partner, lookups.partner)) fail('partner', 'Partner not in list')

  const fromPrice = massNum(row.fromPrice)
  if (fromPrice == null || fromPrice <= 0) fail('fromPrice', 'From Price must be > 0')

  return { ok: badFields.size === 0, badFields, messages }
}

// Filter combo Flag theo cả description hiện ở dòng phụ dropdown — getOptionLabel
// chỉ trả flag nên MUI default filter sẽ KHÔNG match theo description.
function filterFlagOptions<T extends { flag: string; description?: string | null }>(
  opts: T[],
  state: { inputValue: string },
): T[] {
  const q = state.inputValue.trim().toLowerCase()
  if (!q) return opts
  return opts.filter((o) => `${o.flag} ${o.description ?? ''}`.toLowerCase().includes(q))
}

const massCellSx: SxProps<Theme> = { px: 0.5, py: 0.25 }
const massInputSx: SxProps<Theme> = {
  '& .MuiInputBase-root': { fontSize: '0.72rem', py: 0 },
  '& .MuiInputBase-input': { py: '4px', fontSize: '0.72rem' },
  '& .MuiAutocomplete-input': { py: '2px !important', fontSize: '0.72rem' },
}
const massHeaderSx: SxProps<Theme> = {
  fontWeight: 700,
  whiteSpace: 'nowrap',
  fontSize: '0.7rem',
  lineHeight: 1.2,
  py: 0.5,
  px: 0.75,
}

const sumNumericOnPage = (pageRows: SpreadGridRow[], col: ColumnDef): number | null => {
  if (col.kind !== 'number' || NON_SUM.has(col.key)) return null
  let total = 0
  let count = 0
  for (const row of pageRows) {
    const v = row[col.key]
    if (typeof v === 'number' && Number.isFinite(v)) {
      total += v
      count += 1
    }
  }
  return count > 0 ? total : null
}

function str(v: unknown): string {
  if (v == null || v === '') return ''
  return String(v)
}

function cellValue(row: Record<string, unknown>, col: ColumnDef): string {
  const v = row[col.key]
  if (v == null) return ''
  if (col.kind === 'date') return formatAccessDate(v)
  if (col.kind === 'datetime') return formatAccessDateTime(v)
  if (col.kind === 'number' && typeof v === 'number') return formatAccessNumber(v)
  if (typeof v === 'number') return formatAccessNumber(v)
  return String(v)
}

type FilterKind = 'search' | 'date' | 'buysell' | 'partner' | 'type'

interface FilterChecklistProps {
  title: string
  loading: boolean
  options: string[]
  selected: Set<string>
  onToggle: (value: string) => void
  onSelectAll: (options: string[]) => void
  onClear: () => void
  renderLabel?: (value: string) => string
}

function FilterChecklist({
  title,
  loading,
  options,
  selected,
  onToggle,
  onSelectAll,
  onClear,
  renderLabel,
}: FilterChecklistProps) {
  return (
    <Stack spacing={0.25} sx={{ minWidth: 220 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <Stack direction="row" spacing={0.5}>
          <Button
            size="small"
            variant="text"
            disabled={options.length === 0 || selected.size === options.length}
            onClick={() => onSelectAll(options)}
            sx={{ fontSize: '0.7rem', minWidth: 0, px: 0.5, py: 0 }}
          >
            All
          </Button>
          <Button
            size="small"
            variant="text"
            disabled={selected.size === 0}
            onClick={onClear}
            sx={{ fontSize: '0.7rem', minWidth: 0, px: 0.5, py: 0 }}
          >
            Clear
          </Button>
        </Stack>
      </Stack>
      <Box sx={{ maxHeight: 280, overflow: 'auto', borderTop: (t) => `1px solid ${t.palette.divider}` }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
            <CircularProgress size={16} />
          </Box>
        ) : options.length === 0 ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 1, py: 1 }}>
            Không có giá trị.
          </Typography>
        ) : (
          options.map((opt) => {
            const checked = selected.has(opt)
            const label = renderLabel ? renderLabel(opt) : opt
            return (
              <ListItemButton
                key={opt}
                dense
                onClick={() => onToggle(opt)}
                sx={{ py: 0, pl: 0.5, pr: 0.75, minHeight: 28 }}
              >
                <Checkbox
                  edge="start"
                  size="small"
                  checked={checked}
                  tabIndex={-1}
                  disableRipple
                  sx={{ p: 0.25, mr: 0.5 }}
                />
                <ListItemText
                  primary={label}
                  primaryTypographyProps={{ variant: 'body2', noWrap: true, sx: { fontSize: '0.8125rem' } }}
                />
              </ListItemButton>
            )
          })
        )}
      </Box>
    </Stack>
  )
}

interface Props {
  embedded?: boolean
}

type SpreadGridRow = SpreadFormRow

export default function SpreadsOrdersListView({ embedded: _embedded }: Props) {
  const { canEdit } = usePagePermission()
  const snack = useSnackbar()
  /* ── Filter state (template /table-style) ───────────────────────── */
  const [searchTerm, setSearchTerm] = useState('')
  const filterPopover = useFilterPopover<'search'>()
  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: string
    position: ColumnFilterAnchorPosition
  } | null>(null)

  const [pagination, setPagination] = useState({ page: 0, pageSize: FUTURES_ALL_ROWS_PAGE_SIZE })
  const {
    selectedId: selectedRowId,
    setSelectedId: setSelectedRowId,
    toggleSelect: toggleSelectRow,
  } = useRowSelection<string>()

  // Form dialog
  const [formUi, setFormUi] = useState<{ open: boolean; mode: SpreadFormMode; sourceRow: SpreadFormRow | null }>({
    open: false,
    mode: 'new',
    sourceRow: null,
  })

  // Delete dialog (Access-style confirm)
  const [deleteTarget, setDeleteTarget] = useState<{ orderNumber: string; date: string } | null>(null)

  // Mass upload grid (mirrors Access `frmFuturesQrySpreadsOrderAddMany`).
  const [massOpen, setMassOpen] = useState(false)
  const [massRows, setMassRows] = useState<MassUploadRow[]>([emptyMassRow()])
  const [massSelected, setMassSelected] = useState<Set<number>>(() => new Set())
  const [massEpoch, setMassEpoch] = useState(0)

  const queryArgs = {
    page: 1,
    pageSize: FUTURES_ALL_ROWS_PAGE_SIZE,
  }
  const { data, isLoading, isFetching, error, refetch } = useGetFuturesSpreadsQuery(queryArgs)

  // Lookups only fetched while mass dialog is open (RowSources giống Access).
  const symbolLookup = useGetSpreadSymbolsQuery(undefined, { skip: !massOpen })
  const tradeTypeLookup = useGetFuturesOrderTradeTypesQuery(undefined, { skip: !massOpen })
  const partnerLookup = useGetFuturesOrderPartnersQuery(undefined, { skip: !massOpen })
  const flagLookup = useGetFuturesOrderFlagsQuery(undefined, { skip: !massOpen })
  const symbolOptions = useMemo(() => symbolLookup.data ?? [], [symbolLookup.data])
  const tradeTypeOptions = useMemo(() => tradeTypeLookup.data ?? [], [tradeTypeLookup.data])
  const partnerOptions = useMemo(() => partnerLookup.data ?? [], [partnerLookup.data])
  const flagOptions = useMemo(() => flagLookup.data ?? [], [flagLookup.data])

  const massLookups = useMemo<MassLookupOptions>(
    () => ({
      symbol: symbolOptions,
      typeOfTrade: tradeTypeOptions,
      partner: partnerOptions,
      flag: flagOptions.map((f) => f.flag),
    }),
    [symbolOptions, tradeTypeOptions, partnerOptions, flagOptions],
  )

  const [deleteRow, deleteState] = useDeleteFuturesSpreadMutation()
  const [bulkSpreads, bulkState] = useBulkCreateFuturesSpreadsMutation()

  const columns = COLUMNS

  const columnsCtrl = useFuturesColumns({
    mode: 'spreads',
    allColumns: columns,
    storageKey: STORAGE_COLS_SPREADS,
  })
  const { visibleColumns, openMenu: openColMenu } = columnsCtrl
  const { getWidth, startResize } = useColumnWidths('futures.spreads-orders')

  const baseRows = useMemo(
    (): SpreadGridRow[] =>
      (data?.items ?? []).map((r, idx) => ({
        ...(r as Record<string, unknown>),
        id: `${idx}-${String((r as Record<string, unknown>).orderNumber ?? '')}-${String((r as Record<string, unknown>).date ?? '')}`,
      })),
    [data?.items],
  )

  /** Sort: date DESC, order_number ASC (tiebreaker). */
  const sortedRows = useMemo((): SpreadGridRow[] => {
    const out = [...baseRows]
    out.sort((a, b) => {
      const da = toIsoDateOnly(a['date']) ?? ''
      const db = toIsoDateOnly(b['date']) ?? ''
      if (da !== db) return da < db ? 1 : -1
      const oa = str(a['orderNumber'])
      const ob = str(b['orderNumber'])
      if (oa === ob) return 0
      return oa < ob ? -1 : 1
    })
    return out
  }, [baseRows])

  /* ── Filter pipeline: search ∧ per-column ▽ ─────────────────────── */
  const filteredRows = useMemo(
    () =>
      sortedRows.filter(
        (r) =>
          rowMatchesSearch(r, searchTerm, COLUMNS) &&
          rowMatchesAllColumnFilters(r, columnFilters.filters),
      ),
    [sortedRows, searchTerm, columnFilters.filters],
  )

  /* ── Pagination clamp ───────────────────────────────────────────── */
  const displayedRows = useMemo((): SpreadGridRow[] => {
    if (pagination.pageSize === FUTURES_ALL_ROWS_PAGE_SIZE) return filteredRows
    const start = pagination.page * pagination.pageSize
    return filteredRows.slice(start, start + pagination.pageSize)
  }, [filteredRows, pagination.page, pagination.pageSize])

  const firstSelectedRow = useMemo((): SpreadGridRow | null => {
    if (!selectedRowId) return null
    return baseRows.find((r) => r.id === selectedRowId) ?? null
  }, [baseRows, selectedRowId])

  /* ── Per-column popover meta ────────────────────────────────────── */
  const popoverColumn = useMemo(
    () => (colPopover ? COLUMNS.find((c) => c.key === colPopover.key) ?? null : null),
    [colPopover],
  )
  const popoverKind = useMemo(
    () =>
      popoverColumn
        ? inferColumnFilterKind(popoverColumn as never, baseRows)
        : 'text',
    [popoverColumn, baseRows],
  )
  const popoverOptions = useMemo(
    () =>
      popoverKind === 'checkboxes' && colPopover
        ? distinctColumnValues(baseRows, colPopover.key)
        : undefined,
    [popoverKind, colPopover, baseRows],
  )

  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount =
    (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length

  const clearAllFilters = () => {
    setSearchTerm('')
    columnFilters.clearAll()
    setPagination((p) => ({ ...p, page: 0 }))
    setSelectedRowId(null)
    filterPopover.close()
  }

  const totalRowValues = useMemo(() => {
    return new Map(visibleColumns.map((col) => [col.key, sumNumericOnPage(displayedRows, col)] as const))
  }, [displayedRows, visibleColumns])

  const gridKey = `${pagination.page}:${pagination.pageSize}:${displayedRows.length}`

  const openNew = useCallback(() => {
    setFormUi({ open: true, mode: 'new', sourceRow: null })
  }, [])

  const openEdit = useCallback(() => {
    const r = firstSelectedRow
    if (!r) return
    setFormUi({ open: true, mode: 'edit', sourceRow: r })
  }, [firstSelectedRow])

  const openCopy = useCallback(() => {
    const r = firstSelectedRow
    if (!r) return
    setFormUi({ open: true, mode: 'copy', sourceRow: r })
  }, [firstSelectedRow])

  const closeForm = useCallback(() => {
    setFormUi((s) => ({ ...s, open: false }))
    setSelectedRowId(null)
  }, [])

  const removeClick = () => {
    const r = firstSelectedRow
    if (!r?.['orderNumber']) return
    const isoDate = toIsoDateOnly(r['date']) ?? ''
    setDeleteTarget({ orderNumber: String(r['orderNumber']), date: isoDate })
  }

  const confirmRemove = async () => {
    if (!deleteTarget) return
    try {
      await deleteRow(deleteTarget.orderNumber).unwrap()
      snack.success('Successfully Deleted')
      setSelectedRowId(null)
      setDeleteTarget(null)
    } catch (err) {
      snack.error(futuresApiErrorMessage(err))
    }
  }

  const updateMassRow = useCallback((index: number, field: keyof MassUploadRow, value: string) => {
    setMassRows((current) => current.map((row, idx) => (idx === index ? { ...row, [field]: value } : row)))
  }, [])

  // Combobox columns may only accept paste values that exist in the lookup
  // list (case-insensitive). Returns the canonical option label, "" for blank,
  // or null when the value should be rejected. When the lookup hasn't loaded
  // yet (length 0) we let the value through so users can paste before the
  // dropdown options arrive — Upload validation will catch any leftovers.
  const matchOption = useCallback(
    (raw: string, options: string[]): string | null => {
      const v = raw.trim()
      if (!v) return ''
      if (options.length === 0) return v
      const hit = options.find((o) => o.toLowerCase() === v.toLowerCase())
      return hit ?? null
    },
    [],
  )

  const coerceMassPasted = useCallback(
    (field: keyof MassUploadRow, raw: string): string | null => {
      if (field === 'symbol') return matchOption(raw, symbolOptions)
      if (field === 'typeOfTrade') return matchOption(raw, tradeTypeOptions)
      if (field === 'partner') return matchOption(raw, partnerOptions)
      if (field === 'flag') {
        const v = raw.trim()
        if (!v) return ''
        if (flagOptions.length === 0) return v
        const hit = flagOptions.find((o) => (o.flag ?? '').toLowerCase() === v.toLowerCase())
        return hit?.flag ?? null
      }
      return coerceMassField(field, raw)
    },
    [matchOption, symbolOptions, tradeTypeOptions, partnerOptions, flagOptions],
  )

  const handleMassPaste = useCallback(
    (rowIdx: number, colIdx: number, event: React.ClipboardEvent<HTMLElement>) => {
      const text = event.clipboardData?.getData('text') ?? ''
      if (!text) return
      event.preventDefault()
      event.stopPropagation()
      const matrix = parseTsv(text)
      if (matrix.length === 0) return
      const rejected: string[] = []
      setMassRows((current) => {
        const next = current.map((row) => ({ ...row }))
        while (next.length < rowIdx + matrix.length) next.push(emptyMassRow())
        matrix.forEach((cells, r) => {
          const targetRow = next[rowIdx + r]
          cells.forEach((rawCell, c) => {
            const fieldIdx = colIdx + c
            if (fieldIdx >= MASS_FIELDS.length) return
            const field = MASS_FIELDS[fieldIdx]
            const coerced = coerceMassPasted(field, rawCell)
            if (coerced === null) {
              rejected.push(`R${rowIdx + r + 1} ${field}="${rawCell.trim()}"`)
            } else {
              targetRow[field] = coerced
            }
          })
        })
        return next
      })
      if (rejected.length > 0) {
        snack.warning(
          rejected.length === 1
            ? `Rejected: ${rejected[0]} — not in list / not numeric`
            : `Rejected ${rejected.length} cells — not in list / not numeric`,
        )
      }
    },
    [coerceMassPasted, snack],
  )

  // Append a TSV block (from the system clipboard / Excel, or our own Copy output)
  // as brand-new rows at the END of the grid. Drops a leading header row and, if the
  // grid is still the untouched starter row, replaces it. Values that fail coercion
  // (not in list / not numeric) are kept as the raw text so the cross-check flags
  // them red instead of silently dropping the user's paste. Returns rows added.
  const appendMassRowsFromText = useCallback(
    (text: string): number => {
      const matrix = parseTsv(text)
      if (matrix.length === 0) return 0
      const dataRows = matrix.length > 1 && isMassHeaderRow(matrix[0]) ? matrix.slice(1) : matrix
      if (dataRows.length === 0) return 0
      const built = dataRows.map((cells) => {
        const row = emptyMassRow()
        cells.forEach((rawCell, c) => {
          if (c >= MASS_FIELDS.length) return
          const field = MASS_FIELDS[c]
          const coerced = coerceMassPasted(field, rawCell)
          row[field] = coerced === null ? rawCell.trim() : coerced
        })
        return row
      })
      setMassRows((current) => {
        const pristine = current.length === 1 && MASS_FIELDS.every((f) => f === 'date' || !current[0][f])
        return pristine ? built : [...current, ...built]
      })
      setMassEpoch((n) => n + 1)
      return built.length
    },
    [coerceMassPasted],
  )

  const toggleMassRowSelected = useCallback((index: number) => {
    setMassSelected((current) => {
      const next = new Set(current)
      if (next.has(index)) next.delete(index)
      else next.add(index)
      return next
    })
  }, [])

  const toggleMassSelectAll = useCallback(() => {
    setMassSelected((current) => {
      if (current.size === massRows.length && massRows.length > 0) return new Set()
      return new Set(massRows.map((_, idx) => idx))
    })
  }, [massRows])

  const formatMassCellForCopy = (row: MassUploadRow, field: keyof MassUploadRow): string => {
    const value = row[field] ?? ''
    return String(value).replace(/[\t\r\n]/g, ' ')
  }

  const copyMassRows = useCallback(async () => {
    const indices = massSelected.size > 0
      ? Array.from(massSelected).sort((a, b) => a - b)
      : massRows.map((_, idx) => idx)
    if (indices.length === 0) {
      snack.warning('No rows to copy')
      return
    }
    const escapeHtml = (s: string) =>
      s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    // Lead with the column titles so pasting into Excel carries headers.
    const headerTsv = MASS_FIELDS.map((f) => MASS_HEADERS[f]).join('\t')
    const tsv = [
      headerTsv,
      ...indices.map((idx) => MASS_FIELDS.map((f) => formatMassCellForCopy(massRows[idx], f)).join('\t')),
    ].join('\n')
    const headerHtml =
      '<tr>' + MASS_FIELDS.map((f) => `<th>${escapeHtml(MASS_HEADERS[f])}</th>`).join('') + '</tr>'
    const html =
      '<table>' +
      headerHtml +
      indices
        .map(
          (idx) =>
            '<tr>' +
            MASS_FIELDS.map(
              (f) => `<td>${escapeHtml(formatMassCellForCopy(massRows[idx], f))}</td>`,
            ).join('') +
            '</tr>',
        )
        .join('') +
      '</table>'

    // Selection-based execCommand on a contenteditable div. This is the most
    // Excel-friendly path because it puts BOTH text/html and text/plain on the
    // clipboard, works inside MUI dialogs (uses Selection, not focus), and runs
    // on plain HTTP origins where navigator.clipboard is undefined.
    const copyViaSelection = (): boolean => {
      const div = document.createElement('div')
      div.contentEditable = 'true'
      div.innerHTML = html
      div.style.position = 'fixed'
      div.style.top = '-9999px'
      div.style.left = '-9999px'
      div.style.opacity = '0'
      document.body.appendChild(div)
      const selection = window.getSelection()
      if (!selection) {
        document.body.removeChild(div)
        return false
      }
      const savedRanges: Range[] = []
      for (let i = 0; i < selection.rangeCount; i++) savedRanges.push(selection.getRangeAt(i).cloneRange())
      const range = document.createRange()
      range.selectNodeContents(div)
      selection.removeAllRanges()
      selection.addRange(range)
      let ok = false
      try {
        ok = document.execCommand('copy')
      } catch {
        ok = false
      }
      selection.removeAllRanges()
      savedRanges.forEach((r) => selection.addRange(r))
      document.body.removeChild(div)
      return ok
    }

    const copyViaTextarea = (): boolean => {
      const ta = document.createElement('textarea')
      ta.value = tsv
      ta.setAttribute('readonly', '')
      ta.style.position = 'fixed'
      ta.style.top = '-9999px'
      document.body.appendChild(ta)
      const prevActive = document.activeElement as HTMLElement | null
      ta.focus()
      ta.select()
      let ok = false
      try {
        ok = document.execCommand('copy')
      } catch {
        ok = false
      }
      document.body.removeChild(ta)
      prevActive?.focus?.()
      return ok
    }

    let ok = false
    try {
      const ClipboardItemCtor = (window as unknown as { ClipboardItem?: typeof ClipboardItem }).ClipboardItem
      if (ClipboardItemCtor && navigator.clipboard?.write) {
        const item = new ClipboardItemCtor({
          'text/plain': new Blob([tsv], { type: 'text/plain' }),
          'text/html': new Blob([html], { type: 'text/html' }),
        })
        await navigator.clipboard.write([item])
        ok = true
      }
    } catch {
      ok = false
    }
    if (!ok) ok = copyViaSelection()
    if (!ok) {
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(tsv)
          ok = true
        }
      } catch {
        ok = false
      }
    }
    if (!ok) ok = copyViaTextarea()

    if (ok) snack.success(`Copied ${indices.length} row(s) — paste into Excel with Ctrl+V`)
    else snack.error('Browser blocked clipboard access. Open the page over HTTPS or localhost.')
  }, [massSelected, massRows, snack])

  const deleteSelectedMassRows = useCallback(() => {
    if (massSelected.size === 0) {
      snack.warning('Select rows to delete')
      return
    }
    setMassRows((current) => {
      const filtered = current.filter((_, idx) => !massSelected.has(idx))
      return filtered.length === 0 ? [emptyMassRow()] : filtered
    })
    setMassSelected(new Set())
  }, [massSelected, snack])

  const resetMassForm = useCallback(() => {
    setMassRows([emptyMassRow()])
    setMassSelected(new Set())
    setMassEpoch((n) => n + 1)
  }, [])

  const closeMassDialog = useCallback(() => {
    setMassOpen(false)
    setMassSelected(new Set())
    setMassEpoch((n) => n + 1)
  }, [])

  useEffect(() => {
    if (massOpen && massRows.length === 0) {
      const timer = window.setTimeout(() => setMassRows([emptyMassRow()]), 0)
      return () => window.clearTimeout(timer)
    }
  }, [massOpen, massRows.length])

  // While the Mass upload dialog is open, a Ctrl+V that isn't aimed at a specific
  // grid cell (cells handle their own paste and stop propagation) is treated as
  // "paste as new row(s)". This is the reliable path on plain-HTTP origins, where
  // the Clipboard read API is unavailable and only a real paste gesture exposes
  // clipboard data copied from Excel.
  useEffect(() => {
    if (!massOpen) return
    const onDocPaste = (e: ClipboardEvent) => {
      const target = e.target as HTMLElement | null
      // Let real inputs (grid cells, form fields) keep their own paste behaviour;
      // only a paste with no editable target spawns new rows.
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return
      }
      const text = e.clipboardData?.getData('text') ?? ''
      if (!text.trim()) return
      e.preventDefault()
      const added = appendMassRowsFromText(text)
      if (added > 0) snack.success(`Pasted ${added} new row(s)`)
    }
    document.addEventListener('paste', onDocPaste)
    return () => document.removeEventListener('paste', onDocPaste)
  }, [massOpen, appendMassRowsFromText, snack])

  // Live cross-check: re-validate every row whenever a cell (or lookups) change, so
  // the ✓/✗ column, red cells and Upload's enabled state update as you type/paste.
  // Same rule set as `runMassUpload` (Access `btnSave_Click` of
  // frmFuturesQrySpreadsOrderAddMany), factored into validateSpreadRow.
  const massVerdicts = useMemo(
    () => massRows.map((row) => validateSpreadRow(row, massLookups)),
    [massRows, massLookups],
  )
  const massAllValid = massRows.length > 0 && massVerdicts.every((v) => v.ok)
  const massFailCount = massVerdicts.filter((v) => !v.ok).length
  const massCellBg = useCallback(
    (index: number, field: keyof MassUploadRow): string | undefined => {
      const v = massVerdicts[index]
      return v && !v.ok && v.badFields.has(field) ? 'rgba(211,47,47,0.14)' : undefined
    },
    [massVerdicts],
  )

  /** Validation theo Access `btnSave_Click` của frmFuturesQrySpreadsOrderAddMany. */
  const runMassUpload = async () => {
    if (massRows.length === 0) {
      snack.warning('At least 1 row is required')
      return
    }
    // Cross-check gate: never insert while any row fails (Upload is disabled in
    // this state, but guard here too against a stale click).
    const firstBad = massVerdicts.findIndex((v) => !v.ok)
    if (firstBad >= 0) {
      snack.warning(`Row ${firstBad + 1}: ${massVerdicts[firstBad].messages[0]}`)
      return
    }
    try {
      const payloadRows = massRows.map((row) => toMassPayload(row))
      const result = await bulkSpreads({ rows: payloadRows }).unwrap()
      snack.success(`Created ${result.created}/${massRows.length} rows`)
      setMassOpen(false)
      setMassRows([emptyMassRow()])
      setMassSelected(new Set())
      setMassEpoch((n) => n + 1)
    } catch (e: unknown) {
      snack.error(futuresApiErrorMessage(e))
    }
  }

  const deleteConfirmMessage = useMemo(() => {
    if (!deleteTarget) return ''
    const timing = deleteTarget.date ? formatAccessDate(deleteTarget.date) : ''
    return `Do you want to delete Spreads '${deleteTarget.orderNumber}'${timing ? ` with timing '${timing}'` : ''}?`
  }, [deleteTarget])

  const minTableWidth = visibleColumns.length * 96


  return (
    <>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          // Bảng FILL hết flex còn lại (parent bounded) → bỏ khoảng trống dưới bảng.
          '& > .app-dataframe': { flex: 1, minHeight: 0, height: 'auto' },
        }}
      >
        <FilterBar
          hasActiveFilters={hasActiveFilters}
          onClearAll={clearAllFilters}
          activeFilterCount={activeFilterCount}
          rightSlot={
            // Access parity (frmFuturesMainSubFormSpreads): buttons always render;
            // `disabled` per Form_Load SCREEN_ACCESS (canEdit) + btn*_Click row guard.
            // Verbatim click-guards kept inside openEdit/openCopy/removeClick.
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center">
              {/* btnNew_Click: no record required → gate only by SCREEN_ACCESS. */}
              <Button variant="contained" size="small" color="success" sx={actionToolbarBtnSx} disabled={!canEdit} onClick={openNew} startIcon={<AddIcon sx={{ fontSize: 16 }} />} title="New">
                <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>New</Box>
              </Button>
              {/* btnEdit_Click: needs Form![subForm]!order_number ("Please choose a record!") + SCREEN_ACCESS. */}
              <Button variant="outlined" size="small" color="warning" sx={actionToolbarBtnSx} disabled={!firstSelectedRow || !canEdit} onClick={openEdit} startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />} title="Edit">
                <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Edit</Box>
              </Button>
              {/* Copy (web-only): mirror Edit — needs selected row + SCREEN_ACCESS. */}
              <Button variant="outlined" size="small" sx={actionToolbarBtnSx} disabled={!firstSelectedRow || !canEdit} onClick={openCopy} startIcon={<ContentCopyIcon sx={{ fontSize: 16 }} />} title="Copy">
                <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Copy</Box>
              </Button>
              {/* btnDelete_Click: reads selected row order_number + SCREEN_ACCESS. */}
              <Button
                variant="outlined"
                size="small"
                color="error"
                sx={actionToolbarBtnSx}
                disabled={!firstSelectedRow || !canEdit}
                onClick={() => removeClick()}
                startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
                title="Delete"
              >
                <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Delete</Box>
              </Button>
              {/* btnMany (Mass Upload): Access Form_Load does NOT disable it under
                  SCREEN_ACCESS<>1 (only New/Edit/Delete) → not gated by canEdit (1:1 Access). */}
              <Button variant="outlined" size="small" sx={actionToolbarBtnSx} onClick={() => setMassOpen(true)} startIcon={<UploadFileIcon sx={{ fontSize: 16 }} />} title="Mass upload">
                <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Mass upload</Box>
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline', md: 'none' } }}>Mass</Box>
              </Button>
            </Stack>
          }
        >
          <FilterButton
            label="Search"
            active={!!searchTerm.trim()}
            onClick={(e) => filterPopover.open('search', e)}
          />
          <Button
            size="small"
            variant="outlined"
            endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
            onClick={(e) => openColMenu(e.currentTarget)}
            sx={filterBarBtnSx}
            title="Columns"
          >
            <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Columns</Box>
            <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>Cols</Box>
          </Button>
        </FilterBar>

        <Divider sx={{ mb: 0.5 }} />

        <FilterPopover
          open={filterPopover.state !== null}
          anchorEl={filterPopover.state?.anchor ?? null}
          onClose={() => filterPopover.close()}
        >
          {filterPopover.isOpen('search') && (
            <SearchFilterContent
              value={searchTerm}
              onChange={(v) => {
                setSearchTerm(v)
                setPagination((p) => ({ ...p, page: 0 }))
              }}
              placeholder="Order, symbol, partner, legs…"
              onClear={() => filterPopover.close()}
            />
          )}
        </FilterPopover>

        <ColumnFilterPopover
          open={colPopover !== null}
          anchorPosition={colPopover?.position ?? null}
          onClose={() => setColPopover(null)}
          label={popoverColumn?.label ?? ''}
          kind={popoverKind === 'none' ? 'text' : popoverKind}
          value={colPopover ? columnFilters.filters[colPopover.key] : undefined}
          onChange={(v) => {
            if (!colPopover) return
            columnFilters.setFilter(colPopover.key, v)
            setPagination((p) => ({ ...p, page: 0 }))
          }}
          options={popoverOptions}
        />

        <FuturesColumnsMenu controller={columnsCtrl} />

        <Box className="app-dataframe">
          <Box
            className="app-dataframe-toolbar"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 0.5,
              flexWrap: 'wrap',
              minHeight: 24,
              py: 0,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={0.4} sx={{ flexShrink: 0 }}>
              {(isLoading || isFetching) && <CircularProgress size={15} />}
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
                Total: {filteredRows.length}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
                Loaded: {baseRows.length}
              </Typography>
              <TablePagination
                component="div"
                count={filteredRows.length}
                page={pagination.pageSize === FUTURES_ALL_ROWS_PAGE_SIZE ? 0 : pagination.page}
                onPageChange={(_, newPage) => {
                  setPagination((p) => ({ ...p, page: newPage }))
                  setSelectedRowId(null)
                }}
                rowsPerPage={pagination.pageSize}
                onRowsPerPageChange={(e) => {
                  setPagination({ page: 0, pageSize: Number(e.target.value) })
                  setSelectedRowId(null)
                }}
                rowsPerPageOptions={[25, 50, 100, { label: 'All', value: FUTURES_ALL_ROWS_PAGE_SIZE }]}
                labelRowsPerPage="Rows"
                sx={TABLE_PAGINATION_COMPACT}
              />
            </Stack>
          </Box>

          <DataframeBody loading={isLoading} fetching={isFetching}>
            {error ? (
              <BrandState
                tone="error"
                title="Could not load Spreads data"
                description="Please try again later."
                action={
                  <Button size="small" variant="outlined" onClick={() => void refetch()}>
                    Retry
                  </Button>
                }
              />
            ) : isLoading && baseRows.length === 0 ? (
              <BrandState tone="loading" title="Loading…" description="Please wait." />
            ) : displayedRows.length === 0 ? (
              <BrandState
                title="No data"
                description={
                  hasActiveFilters
                    ? 'No rows match the filter on the current page. Try clearing the filter or changing pages.'
                    : 'Change the search filter or add a new Spread.'
                }
              />
            ) : (
              <TableVirtuoso
                key={gridKey}
                style={{ height: '100%' }}
                data={displayedRows}
                fixedHeaderContent={() => (
                  <TableRow>
                    {visibleColumns.map((col) => {
                      const isFrozen = FROZEN_KEYS.has(col.key)
                      const w = getWidth(col.key) ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[col.key] : undefined)
                      const headerStyle: CSSProperties = {}
                      if (w) { headerStyle.width = w; headerStyle.minWidth = w; headerStyle.maxWidth = w }
                      if (isFrozen) {
                        headerStyle.position = 'sticky'
                        headerStyle.top = 0
                        headerStyle.left = 0
                        headerStyle.zIndex = 100
                      }
                      return (
                        <TableCell
                          key={col.key}
                          align={col.align}
                          className="app-table-header-cell"
                          style={Object.keys(headerStyle).length ? headerStyle : undefined}
                          sx={{
                            whiteSpace: 'nowrap',
                            // Cho phép bôi đen + copy tên cột (header).
                            userSelect: 'text',
                            ...(isFrozen ? {} : { position: 'relative' }),
                          }}
                        >
                          {col.label}
                          <ColumnFilterIcon
                            active={columnFilters.isActive(col.key)}
                            onClick={(position) =>
                              setColPopover({ key: col.key, position })
                            }
                            ariaLabel={`Filter ${col.label}`}
                          />
                          <ResizeHandle onMouseDown={(e) => startResize(col.key, e)} />
                        </TableCell>
                      )
                    })}
                  </TableRow>
                )}
                fixedFooterContent={() => (
                  <TableRow>
                    {visibleColumns.map((col, index) => {
                      const isFrozen = FROZEN_KEYS.has(col.key)
                      const total = totalRowValues.get(col.key)
                      const val = total == null ? '' : formatAccessNumber(total)
                      const w = getWidth(col.key) ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[col.key] : undefined)
                      return (
                        <TableCell
                          key={col.key}
                          align={col.align}
                          className={isFrozen ? 'app-sticky-col' : undefined}
                          style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                          sx={{
                            fontWeight: 700,
                            whiteSpace: 'nowrap',
                            py: 0.5,
                            bgcolor: 'background.paper',
                            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {index === 0 ? 'Total' : val}
                        </TableCell>
                      )
                    })}
                  </TableRow>
                )}
                itemContent={(_: number, row: SpreadGridRow) =>
                  visibleColumns.map((col) => {
                    const isFrozen = FROZEN_KEYS.has(col.key)
                    const val = cellValue(row, col)
                    const w = getWidth(col.key) ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[col.key] : undefined)
                    const wrap = getWidth(col.key) != null
                    if (wrap) {
                      return (
                        <TableCell
                          key={col.key}
                          align={col.align}
                          className={isFrozen ? 'app-sticky-col' : undefined}
                          style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                          sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere', py: 0.5, verticalAlign: 'top' }}
                        >
                          <Box component="span" className="app-table-cell-wrap">
                            {val || '-'}
                          </Box>
                        </TableCell>
                      )
                    }
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align}
                        className={isFrozen ? 'app-sticky-col' : undefined}
                        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                        sx={{ whiteSpace: 'nowrap', py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'top' }}
                      >
                        <Box
                          component="span"
                          className="app-table-cell-text"
                          title={val || '-'}
                          sx={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}
                        >
                          {val || '-'}
                        </Box>
                      </TableCell>
                    )
                  })
                }
                components={{
                  Table: (props: any) => <Table {...props} size="small" stickyHeader sx={{ minWidth: minTableWidth, width: '100%' }} />,
                  TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
                  TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
                  TableRow: (props: any) => {
                    const idx = Number((props as Record<string, unknown>)['data-item-index'])
                    const r = Number.isFinite(idx) ? displayedRows[idx] : null
                    const rid = r?.id ?? null
                    return (
                      <TableRow
                        {...props}
                        hover
                        selected={rid != null && rid === selectedRowId}
                        sx={{ cursor: 'pointer' }}
                        onClick={() => {
                          if (!rid) return
                          toggleSelectRow(rid)
                        }}
                      />
                    )
                  },
                }}
              />
            )}
          </DataframeBody>
        </Box>
      </Box>

      <SpreadsFormDialog
        open={formUi.open}
        mode={formUi.mode}
        sourceRow={formUi.sourceRow}
        onClose={closeForm}
        canEdit={canEdit}
      />

      {/* Delete dialog — cùng chrome với form New/Edit/Copy. */}
      <Dialog
        open={deleteTarget != null}
        onClose={() => setDeleteTarget(null)}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            maxHeight: 'min(280px, 60vh)',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <DialogTitle sx={futuresFormDialogTitleSx}>
          <Typography
            component="span"
            noWrap
            title="Futures Control - Spreads (Delete)"
            sx={futuresFormDialogTitleTextSx}
          >
            Futures Control - Spreads (Delete)
          </Typography>
          <Box sx={futuresFormDialogActionsBoxSx}>
            <Button
              variant="contained"
              size="small"
              color="error"
              onClick={() => void confirmRemove()}
              disabled={deleteState.isLoading || !canEdit}
              sx={futuresFormDialogActionBtnSx}
            >
              {deleteState.isLoading ? <CircularProgress size={14} color="inherit" /> : 'Delete'}
            </Button>
            <Button variant="outlined" size="small" onClick={() => setDeleteTarget(null)} sx={futuresFormDialogActionBtnSx}>
              Cancel
            </Button>
          </Box>
        </DialogTitle>
        <Divider sx={{ flexShrink: 0 }} />
        <DialogContent
          dividers
          sx={{
            pt: 0.75,
            overflowY: 'auto',
            flex: '1 1 auto',
            minHeight: 0,
          }}
        >
          <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
            {deleteConfirmMessage}
          </Typography>
        </DialogContent>
      </Dialog>

      <Dialog
        open={massOpen}
        onClose={closeMassDialog}
        fullWidth
        maxWidth="xl"
        scroll="paper"
        PaperProps={futuresSaleContractDialogPaperProps}
      >
        <DialogTitle sx={futuresSaleContractDialogTitleSx}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ width: '100%' }}>
            <Typography component="span" noWrap title="Mass Upload - Spreads" sx={futuresFormDialogTitleTextSx}>
              Mass Upload - Spreads
            </Typography>
            <Box sx={futuresFormDialogActionsBoxSx}>
              <Tooltip title={massFailCount > 0 ? `${massFailCount} row(s) failed cross-check — fix to enable Upload` : ''}>
                <span>
                  <Button
                    variant="contained"
                    size="small"
                    color="success"
                    disabled={bulkState.isLoading || massRows.length === 0 || !massAllValid}
                    onClick={() => void runMassUpload()}
                    sx={futuresFormDialogActionBtnSx}
                  >
                    {bulkState.isLoading ? <CircularProgress size={14} color="inherit" /> : 'Upload'}
                  </Button>
                </span>
              </Tooltip>
              <Button variant="outlined" size="small" onClick={closeMassDialog} sx={futuresFormDialogActionBtnSx}>
                Cancel
              </Button>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={futuresSaleContractDialogContentSx}>
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center" flexWrap="wrap">
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, textTransform: 'none !important', letterSpacing: 'normal', color: massFailCount > 0 ? 'error.main' : 'success.main' }}
              >
                {massFailCount > 0
                  ? `Cross-check: ${massFailCount} of ${massRows.length} row(s) need fixing`
                  : `Cross-check passed — ${massRows.length} row(s) ready`}
              </Typography>
              <Stack direction="row" spacing={0.75}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                  onClick={() => setMassRows((current) => [...current, emptyMassRow()])}
                  sx={{ fontSize: '0.7rem', py: 0.25, px: 0.875, minHeight: 26, textTransform: 'none' }}
                >
                  Add row
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
                  onClick={() => void copyMassRows()}
                  sx={{ fontSize: '0.7rem', py: 0.25, px: 0.875, minHeight: 26, textTransform: 'none' }}
                >
                  {massSelected.size > 0 ? `Copy (${massSelected.size})` : 'Copy all'}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteOutlineIcon sx={{ fontSize: 14 }} />}
                  disabled={massSelected.size === 0}
                  onClick={deleteSelectedMassRows}
                  sx={{ fontSize: '0.7rem', py: 0.25, px: 0.875, minHeight: 26, textTransform: 'none' }}
                >
                  {massSelected.size > 0 ? `Delete (${massSelected.size})` : 'Delete selected'}
                </Button>
                <Button
                  size="small"
                  variant="text"
                  onClick={resetMassForm}
                  sx={{ fontSize: '0.7rem', py: 0.25, px: 0.875, minHeight: 26, textTransform: 'none' }}
                >
                  Reset
                </Button>
              </Stack>
            </Stack>

            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', textTransform: 'none !important', letterSpacing: 'normal', display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              <ContentPasteIcon sx={{ fontSize: 14 }} />
              Symbol ≥ 6 chars (system builds the full symbol from Roll Step). Copy rows in Excel (or click Copy), then press Ctrl+V to paste them as new rows. You can also paste directly into a cell.
            </Typography>

            <Box sx={{ maxHeight: '55vh', overflow: 'auto', border: '1px solid #bdbdbd', bgcolor: '#fff' }}>
              <Table size="small" stickyHeader sx={{ width: '100%', tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...massHeaderSx, width: 44 }} />
                    <TableCell padding="checkbox" align="left" sx={{ bgcolor: '#fafafa', width: 44, pl: 0.75, pr: 2 }}>
                      <Checkbox
                        size="small"
                        indeterminate={massSelected.size > 0 && massSelected.size < massRows.length}
                        checked={massRows.length > 0 && massSelected.size === massRows.length}
                        onChange={toggleMassSelectAll}
                        sx={{ p: 0.25, ml: 0 }}
                      />
                    </TableCell>
                    <TableCell sx={{ ...massHeaderSx, width: 36, textAlign: 'center', px: 0.25 }} title="Cross-check status">✓</TableCell>
                    <TableCell sx={{ ...massHeaderSx, width: 118, pl: 1.25 }}>Date *</TableCell>
                    <TableCell sx={{ ...massHeaderSx, width: 90 }}>Buy/Sell *</TableCell>
                    <TableCell sx={{ ...massHeaderSx, width: 130 }}>Symbol *</TableCell>
                    <TableCell sx={{ ...massHeaderSx, width: 80 }}>Roll Step *</TableCell>
                    <TableCell sx={{ ...massHeaderSx, width: 95 }}>Filled Price *</TableCell>
                    <TableCell sx={{ ...massHeaderSx, width: 110 }}>Type of Trade *</TableCell>
                    <TableCell sx={{ ...massHeaderSx, width: 100 }}>Flag *</TableCell>
                    <TableCell sx={{ ...massHeaderSx, width: 70 }}>Lot *</TableCell>
                    <TableCell sx={{ ...massHeaderSx, width: 100 }}>Partner *</TableCell>
                    <TableCell sx={{ ...massHeaderSx, width: 95 }}>From Price *</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {massRows.map((row, index) => {
                    const selected = massSelected.has(index)
                    const verdict = massVerdicts[index]
                    return (
                      <TableRow key={`${massEpoch}-${index}`} hover selected={selected}>
                        <TableCell sx={{ ...massCellSx, width: 44, verticalAlign: 'middle' }}>
                          <Tooltip title="Delete row">
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                disabled={massRows.length === 1}
                                onClick={() => {
                                  setMassRows((current) => current.filter((_, idx) => idx !== index))
                                  setMassSelected((current) => {
                                    const next = new Set<number>()
                                    current.forEach((i) => {
                                      if (i === index) return
                                      next.add(i > index ? i - 1 : i)
                                    })
                                    return next
                                  })
                                }}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                        <TableCell padding="checkbox" align="left" sx={{ width: 44, pl: 0.75, pr: 2 }}>
                          <Checkbox
                            size="small"
                            checked={selected}
                            onChange={() => toggleMassRowSelected(index)}
                            sx={{ p: 0.25, ml: 0 }}
                          />
                        </TableCell>
                        <TableCell sx={{ ...massCellSx, width: 36, textAlign: 'center', verticalAlign: 'middle', px: 0.25 }}>
                          {verdict?.ok ? (
                            <CheckCircleOutlineIcon sx={{ fontSize: 17, color: 'success.main', verticalAlign: 'middle' }} />
                          ) : (
                            <Tooltip title={<Box sx={{ whiteSpace: 'pre-line' }}>{verdict?.messages.join('\n')}</Box>}>
                              <ErrorOutlineIcon sx={{ fontSize: 17, color: 'error.main', verticalAlign: 'middle' }} />
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell sx={{ ...massCellSx, minWidth: 128, pl: 1.25, bgcolor: massCellBg(index, 'date') }} onPasteCapture={(e) => handleMassPaste(index, 0, e)}>
                          <TextField
                            size="small"
                            type="date"
                            value={safeDateValue(row.date)}
                            onChange={(e) => updateMassRow(index, 'date', e.target.value)}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            sx={massInputSx}
                          />
                        </TableCell>
                        <TableCell sx={{ ...massCellSx, minWidth: 100, bgcolor: massCellBg(index, 'buyOrSell') }} onPasteCapture={(e) => handleMassPaste(index, 1, e)}>
                          <Autocomplete
                            size="small"
                            options={[...BUY_SELL_OPTIONS]}
                            value={row.buyOrSell || null}
                            onChange={(_, v) => updateMassRow(index, 'buyOrSell', v ?? '')}
                            onInputChange={(_, v, reason) => {
                              if (reason === 'input') updateMassRow(index, 'buyOrSell', v.toUpperCase())
                            }}
                            freeSolo
                            renderInput={(params) => <TextField {...params} placeholder="BUY / SELL" sx={massInputSx} />}
                          />
                        </TableCell>
                        <TableCell sx={{ ...massCellSx, minWidth: 140, bgcolor: massCellBg(index, 'symbol') }} onPasteCapture={(e) => handleMassPaste(index, 2, e)}>
                          <Autocomplete
                            size="small"
                            options={symbolOptions}
                            value={row.symbol || null}
                            onChange={(_, v) => updateMassRow(index, 'symbol', v ?? '')}
                            onInputChange={(_, v, reason) => {
                              if (reason === 'input') updateMassRow(index, 'symbol', v)
                            }}
                            freeSolo
                            loading={symbolLookup.isLoading}
                            renderInput={(params) => <TextField {...params} placeholder="Symbol (≥ 6 chars)" sx={massInputSx} />}
                          />
                        </TableCell>
                        <TableCell sx={{ ...massCellSx, minWidth: 90, bgcolor: massCellBg(index, 'rollStep') }} onPasteCapture={(e) => handleMassPaste(index, 3, e)}>
                          <TextField
                            size="small"
                            value={row.rollStep}
                            onChange={(e) => {
                              const v = e.target.value
                              if (v === '' || /^-?\d*$/.test(v)) updateMassRow(index, 'rollStep', v)
                            }}
                            fullWidth
                            inputProps={{ inputMode: 'numeric', style: { textAlign: 'right' } }}
                            sx={massInputSx}
                          />
                        </TableCell>
                        <TableCell sx={{ ...massCellSx, minWidth: 110, bgcolor: massCellBg(index, 'filledPrice') }} onPasteCapture={(e) => handleMassPaste(index, 4, e)}>
                          <TextField
                            size="small"
                            value={row.filledPrice}
                            onChange={(e) => {
                              const v = e.target.value
                              if (v === '' || /^-?\d*\.?\d*$/.test(v)) updateMassRow(index, 'filledPrice', v)
                            }}
                            fullWidth
                            inputProps={{ inputMode: 'decimal', style: { textAlign: 'right' } }}
                            sx={massInputSx}
                          />
                        </TableCell>
                        <TableCell sx={{ ...massCellSx, minWidth: 140, bgcolor: massCellBg(index, 'typeOfTrade') }} onPasteCapture={(e) => handleMassPaste(index, 5, e)}>
                          <Autocomplete
                            size="small"
                            options={tradeTypeOptions}
                            value={row.typeOfTrade || null}
                            onChange={(_, v) => updateMassRow(index, 'typeOfTrade', v ?? '')}
                            onInputChange={(_, v, reason) => {
                              if (reason === 'input') updateMassRow(index, 'typeOfTrade', v)
                            }}
                            freeSolo
                            loading={tradeTypeLookup.isLoading}
                            renderInput={(params) => <TextField {...params} placeholder="Type of Trade" sx={massInputSx} />}
                          />
                        </TableCell>
                        <TableCell sx={{ ...massCellSx, minWidth: 120, bgcolor: massCellBg(index, 'flag') }} onPasteCapture={(e) => handleMassPaste(index, 6, e)}>
                          <Autocomplete
                            size="small"
                            options={flagOptions}
                            getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.flag)}
                            isOptionEqualToValue={(opt, v) =>
                              (typeof opt === 'string' ? opt : opt.flag) === (typeof v === 'string' ? v : v?.flag)
                            }
                            value={row.flag ? flagOptions.find((o) => o.flag === row.flag) ?? { flag: row.flag, description: '' } : null}
                            onChange={(_, v) => updateMassRow(index, 'flag', typeof v === 'string' ? v : v?.flag ?? '')}
                            onInputChange={(_, v, reason) => {
                              if (reason === 'input') updateMassRow(index, 'flag', v)
                            }}
                            freeSolo
                            loading={flagLookup.isLoading}
                            filterOptions={filterFlagOptions}
                            renderOption={(props, opt) => (
                              <li {...props} key={typeof opt === 'string' ? opt : opt.flag}>
                                <Box>
                                  <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>{typeof opt === 'string' ? opt : opt.flag}</Typography>
                                  {typeof opt !== 'string' && opt.description ? (
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                                      {opt.description}
                                    </Typography>
                                  ) : null}
                                </Box>
                              </li>
                            )}
                            renderInput={(params) => <TextField {...params} placeholder="Flag" sx={massInputSx} />}
                          />
                        </TableCell>
                        <TableCell sx={{ ...massCellSx, minWidth: 80, bgcolor: massCellBg(index, 'lot') }} onPasteCapture={(e) => handleMassPaste(index, 7, e)}>
                          <TextField
                            size="small"
                            value={row.lot}
                            onChange={(e) => {
                              const v = e.target.value
                              if (v === '' || /^-?\d*\.?\d*$/.test(v)) updateMassRow(index, 'lot', v)
                            }}
                            fullWidth
                            inputProps={{ inputMode: 'decimal', style: { textAlign: 'right' } }}
                            sx={massInputSx}
                          />
                        </TableCell>
                        <TableCell sx={{ ...massCellSx, minWidth: 120, bgcolor: massCellBg(index, 'partner') }} onPasteCapture={(e) => handleMassPaste(index, 8, e)}>
                          <Autocomplete
                            size="small"
                            options={partnerOptions}
                            value={row.partner || null}
                            onChange={(_, v) => updateMassRow(index, 'partner', v ?? '')}
                            onInputChange={(_, v, reason) => {
                              if (reason === 'input') updateMassRow(index, 'partner', v)
                            }}
                            freeSolo
                            loading={partnerLookup.isLoading}
                            renderInput={(params) => <TextField {...params} placeholder="Partner" sx={massInputSx} />}
                          />
                        </TableCell>
                        <TableCell sx={{ ...massCellSx, minWidth: 110, bgcolor: massCellBg(index, 'fromPrice') }} onPasteCapture={(e) => handleMassPaste(index, 9, e)}>
                          <TextField
                            size="small"
                            value={row.fromPrice}
                            onChange={(e) => {
                              const v = e.target.value
                              if (v === '' || /^-?\d*\.?\d*$/.test(v)) updateMassRow(index, 'fromPrice', v)
                            }}
                            fullWidth
                            inputProps={{ inputMode: 'decimal', style: { textAlign: 'right' } }}
                            sx={massInputSx}
                          />
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </Box>
            {massFailCount > 0 && (
              <Alert severity="warning" sx={{ py: 0.5, '& .MuiAlert-message': { py: 0.25 } }}>
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.25, textTransform: 'none !important', letterSpacing: 'normal' }}>
                  Cross-check issues — fix before Upload
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {massVerdicts.flatMap((v, i) =>
                    v.ok ? [] : [
                      <Typography key={i} component="li" variant="caption" sx={{ display: 'list-item', textTransform: 'none !important', letterSpacing: 'normal' }}>
                        {`Row ${i + 1}: ${v.messages.join(', ')}`}
                      </Typography>,
                    ],
                  )}
                </Box>
              </Alert>
            )}
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  )
}
